// src/admin/components/OrderDetailsModal.tsx

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { adminApi } from '../../utils/api';
import { OrderItem as ApiOrderItem, Order as ApiOrder } from '../../types';

interface OrderDetailsModalProps {
  orderId: string;
  onClose: () => void;
}

interface OrderItem extends Omit<ApiOrderItem, 'price'> {
  price: number; // Override price to be number instead of string
}

interface OrderDetails {
  id: string;
  order_number: string;
  user_id: number | null;
  status: string;
  total: number;
  shipping_fee: string;
  payment_method: string;
  payment_status: string;
  notes: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  shipping_address: string;
  shipping_status: string;
  tracking_number: string;
  tracking_url: string;
  estimated_delivery_date: string | null;
  actual_delivery_date: string | null;
  shipping_provider: string;
  delivery_instructions: string;
  created_at: string;
  updated_at: string;
  items: number;
  paymentMethod: string;
  itemsDetails: OrderItem[];
}

const backendBaseURL = 'https://backend1.shynerzbabyshop.com';

function getFullImageUrl(url: string | undefined): string {
  if (!url) return 'https://via.placeholder.com/80?text=No+Image';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return backendBaseURL + url;
}

// VariationModal component rendered in a portal
const VariationModal: React.FC<{
  variationData: Record<string, any>;
  onClose: () => void;
}> = ({ variationData, onClose }) => {
  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[9999]">
      <div className="bg-white rounded-lg shadow-lg p-6 w-80">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Variation Details</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>
        <table className="w-full">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-2 py-1 text-left">Key</th>
              <th className="px-2 py-1 text-left">Value</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(variationData).map(([key, value]) => (
              <tr key={key}>
                <td className="border px-2 py-1">{key}</td>
                <td className="border px-2 py-1">{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>,
    document.body
  );
};

const OrderDetailsModal: React.FC<OrderDetailsModalProps> = ({ orderId, onClose }) => {
  const [orderDetails, setOrderDetails] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showVariationModal, setShowVariationModal] = useState<boolean>(false);
  const [selectedVariationData, setSelectedVariationData] = useState<Record<string, any> | null>(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const response = await adminApi.orders.getById(orderId);
        // Transform the response to match the OrderDetails interface
        const transformedOrder: OrderDetails = {
          ...response.data,
          order_number: response.data.id, // Ensure we use the UUID as order_number
          total: parseFloat(response.data.total_amount),
          items: response.data.itemsDetails?.length || 0,
          paymentMethod: response.data.payment_method,
          itemsDetails: response.data.itemsDetails?.map(item => ({
            ...item,
            price: parseFloat(item.price)
          })) || []
        };
        setOrderDetails(transformedOrder);
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError('Failed to fetch order details.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [orderId]);

  // Parse the variation details JSON string and show the VariationModal
  const handleViewVariations = (variationDetailsStr: string | undefined) => {
    if (!variationDetailsStr) return;
    try {
      const parsed = JSON.parse(variationDetailsStr);
      setSelectedVariationData(parsed);
      setShowVariationModal(true);
    } catch (e) {
      console.error('Error parsing variation details:', e);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !orderDetails) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="bg-white p-6 rounded-lg shadow-lg">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Order Details</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-red-500">{error || 'Order details not available.'}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="bg-white w-11/12 md:max-w-3xl rounded-lg shadow-lg overflow-y-auto max-h-full p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold">Order Details</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <strong>Order ID:</strong> {orderDetails.order_number}
            </div>
            <div>
              <strong>Customer:</strong> {orderDetails.customer_name}
            </div>
            <div>
              <strong>Date:</strong> {new Date(orderDetails.created_at).toLocaleString()}
            </div>
            <div>
              <strong>Total:</strong> KES {orderDetails.total.toLocaleString()}
            </div>
            <div>
              <strong>Payment Method:</strong> {orderDetails.paymentMethod}
            </div>
            <div>
              <strong>Payment Status:</strong>{' '}
              {orderDetails.payment_status
                ? orderDetails.payment_status.charAt(0).toUpperCase() + orderDetails.payment_status.slice(1)
                : 'N/A'}
            </div>
            {orderDetails.tracking_url && (
              <div>
                <strong>Tracking URL:</strong>{' '}
                <a
                  href={orderDetails.tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-pink-500 underline"
                >
                  Track Shipment
                </a>
              </div>
            )}

            <h3 className="text-xl font-semibold mt-4">Items</h3>
            <table className="w-full border">
              <thead>
                <tr className="bg-gray-100">
                  <th className="px-4 py-2 text-left">Product</th>
                  <th className="px-4 py-2 text-left">Variations</th>
                  <th className="px-4 py-2 text-left">Quantity</th>
                  <th className="px-4 py-2 text-left">Price</th>
                  <th className="px-4 py-2 text-left">Total</th>
                </tr>
              </thead>
              <tbody>
                {orderDetails.itemsDetails.map((item, index) => (
                  <tr key={index} className="border-t">
                    <td className="px-4 py-2 flex items-center gap-2">
                      {item.product_image ? (
                        <img
                          src={getFullImageUrl(item.product_image)}
                          alt={item.product_name}
                          className="w-10 h-10 object-cover rounded"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-gray-200 flex items-center justify-center rounded">
                          No Image
                        </div>
                      )}
                      <span>{item.product_name}</span>
                    </td>
                    <td className="px-4 py-2">
                      {item.variation_details ? (
                        <button
                          onClick={() => handleViewVariations(item.variation_details)}
                          className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                        >
                          View Variations
                        </button>
                      ) : (
                        'N/A'
                      )}
                    </td>
                    <td className="px-4 py-2">{item.quantity}</td>
                    <td className="px-4 py-2">KES {item.price.toLocaleString()}</td>
                    <td className="px-4 py-2">
                      KES {(item.price * item.quantity).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {showVariationModal && selectedVariationData && (
        <VariationModal
          variationData={selectedVariationData}
          onClose={() => {
            setShowVariationModal(false);
            setSelectedVariationData(null);
          }}
        />
      )}
    </>
  );
};

export default OrderDetailsModal;